/**
 * pre186: Berater-Chat als 1:1 P2P-Transport (analog recordingTransfer).
 * - Eine RTCPeerConnection je Peer; Inhalte laufen über einen DTLS-gesicherten DataChannel
 *   ('bamoa-chat'). Der Server sieht NIE Inhalte — er relayt nur SDP/ICE (chat:signal).
 * - Deterministischer Initiator (kleinere userId offert) verhindert Glare.
 * - Kein Verlauf, keine Persistenz: eingehende/ausgehende Nachrichten landen nur im RAM-Store.
 */
import { loadIceServers } from './iceServers';
import { useChatStore } from '@/state/chatStore';

type Sig =
  | { t: 'offer'; sdp: RTCSessionDescriptionInit }
  | { t: 'answer'; sdp: RTCSessionDescriptionInit }
  | { t: 'ice'; candidate: RTCIceCandidateInit }
  | { t: 'need' };

interface Peer { pc: RTCPeerConnection; dc: RTCDataChannel | null }

const uuid = (): string =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

class ChatTransport {
  private myId = '';
  private sendSignal: ((to: string, payload: Sig) => void) | null = null;
  private peers = new Map<string, Peer>();
  private pending = new Map<string, string[]>(); // ausgehende Nachrichten vor dc.open
  // pre190: Companion-Spiegel. mirrorPush = A/V pusht Updates an den Companion;
  // mirrorSend = Companion routet Senden/Öffnen an die A/V-Session (kein eigenes P2P).
  mirrorPush: ((obj: unknown) => void) | null = null;
  mirrorSend: ((obj: unknown) => void) | null = null;

  attach(myId: string, sendSignal: (to: string, payload: Sig) => void): void {
    this.myId = myId;
    this.sendSignal = sendSignal;
  }

  detach(): void {
    for (const [, p] of this.peers) { try { p.pc.close(); } catch { /* */ } }
    this.peers.clear();
    this.pending.clear();
    this.sendSignal = null;
  }

  /** kleinere userId = Initiator (offert), damit nie beide gleichzeitig offerieren. */
  private amOfferer(peerId: string): boolean { return this.myId !== '' && this.myId < peerId; }

  private signal(to: string, payload: Sig): void { this.sendSignal?.(to, payload); }
  private setStatus(peerId: string, st: 'connecting' | 'open' | 'closed'): void {
    useChatStore.getState().setStatus(peerId, st);
    this.mirrorPush?.({ t: 'status', peerId, st });
  }

  /** pre192: Lesestatus lokal setzen UND an die andere Sitzung (A/V↔Companion) spiegeln. */
  markRead(peerId: string): void {
    useChatStore.getState().markRead(peerId);
    this.mirrorPush?.({ t: 'read', peerId });
    this.mirrorSend?.({ t: 'read', peerId });
  }

  /** Sicherstellen, dass eine Verbindung zum Peer existiert (oder im Aufbau ist). */
  async ensureConnection(peerId: string): Promise<void> {
    // pre190: Companion → an die A/V-Session melden (dort wird das echte P2P aufgebaut).
    if (this.mirrorSend) { this.mirrorSend({ t: 'open', peerId, name: useChatStore.getState().names[peerId] ?? '' }); return; }
    if (this.peers.has(peerId) || !peerId || peerId === this.myId) return;
    if (this.amOfferer(peerId)) await this.createOfferer(peerId);
    else { this.setStatus(peerId, 'connecting'); this.signal(peerId, { t: 'need' }); }
  }

  private async createOfferer(peerId: string): Promise<void> {
    if (this.peers.has(peerId)) return;
    const iceServers = await loadIceServers();
    const pc = new RTCPeerConnection({ iceServers });
    const peer: Peer = { pc, dc: null };
    this.peers.set(peerId, peer);
    this.wirePc(peerId, pc);
    this.bindDc(peerId, pc.createDataChannel('bamoa-chat', { ordered: true }));
    this.setStatus(peerId, 'connecting');
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    this.signal(peerId, { t: 'offer', sdp: offer });
  }

  private async createAnswerer(peerId: string, sdp: RTCSessionDescriptionInit): Promise<void> {
    const iceServers = await loadIceServers();
    const pc = new RTCPeerConnection({ iceServers });
    const peer: Peer = { pc, dc: null };
    this.peers.set(peerId, peer);
    this.wirePc(peerId, pc);
    pc.ondatachannel = (ev) => this.bindDc(peerId, ev.channel);
    this.setStatus(peerId, 'connecting');
    await pc.setRemoteDescription(sdp);
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    this.signal(peerId, { t: 'answer', sdp: answer });
  }

  private wirePc(peerId: string, pc: RTCPeerConnection): void {
    pc.onicecandidate = (e) => { if (e.candidate) this.signal(peerId, { t: 'ice', candidate: e.candidate.toJSON() }); };
    pc.onconnectionstatechange = () => {
      if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) {
        this.setStatus(peerId, 'closed');
        this.drop(peerId);
      }
    };
  }

  private bindDc(peerId: string, dc: RTCDataChannel): void {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    peer.dc = dc;
    dc.onopen = () => {
      this.setStatus(peerId, 'open');
      const q = this.pending.get(peerId);
      if (q) { for (const m of q) { try { dc.send(m); } catch { /* */ } } this.pending.delete(peerId); }
    };
    dc.onclose = () => this.setStatus(peerId, 'closed');
    dc.onmessage = (ev) => {
      if (typeof ev.data !== 'string') return;
      try {
        const m = JSON.parse(ev.data);
        if (m?.t === 'msg' && typeof m.body === 'string') {
          const name = useChatStore.getState().names[peerId] || 'Berater';
          const inMsg = {
            id: typeof m.id === 'string' ? m.id : uuid(),
            body: m.body, ts: typeof m.ts === 'number' ? m.ts : Date.now(), mine: false,
          };
          useChatStore.getState().addMessage(peerId, name, inMsg);
          this.mirrorPush?.({ t: 'msg', peerId, name, msg: inMsg });
        }
      } catch { /* */ }
    };
  }

  async handleSignal(from: string, payload: Sig): Promise<void> {
    if (!from || !payload) return;
    try {
      if (payload.t === 'need') {
        if (this.amOfferer(from)) await this.createOfferer(from);
        return;
      }
      if (payload.t === 'offer') {
        if (!this.peers.has(from)) await this.createAnswerer(from, payload.sdp);
        else await this.peers.get(from)!.pc.setRemoteDescription(payload.sdp);
        return;
      }
      const peer = this.peers.get(from);
      if (!peer) return;
      if (payload.t === 'answer') await peer.pc.setRemoteDescription(payload.sdp);
      else if (payload.t === 'ice') await peer.pc.addIceCandidate(payload.candidate);
    } catch { /* */ }
  }

  /** Nachricht senden: lokal in den RAM-Store + über den DataChannel (oder puffern). */
  send(peerId: string, name: string, body: string): void {
    const text = body.trim();
    if (!text) return;
    // pre190: Companion → an die A/V-Session; dort wird real gesendet und zurückgespiegelt.
    if (this.mirrorSend) { this.mirrorSend({ t: 'send', peerId, name, body: text }); return; }
    const id = uuid();
    const ts = Date.now();
    const wire = JSON.stringify({ t: 'msg', id, body: text, ts });
    const peer = this.peers.get(peerId);
    if (peer?.dc && peer.dc.readyState === 'open') {
      try { peer.dc.send(wire); } catch { this.queue(peerId, wire); }
    } else {
      this.queue(peerId, wire);
      void this.ensureConnection(peerId);
    }
    const outMsg = { id, body: text, ts, mine: true };
    useChatStore.getState().addMessage(peerId, name, outMsg);
    this.mirrorPush?.({ t: 'msg', peerId, name, msg: outMsg });
  }

  private queue(peerId: string, wire: string): void {
    const q = this.pending.get(peerId) ?? [];
    q.push(wire);
    this.pending.set(peerId, q);
  }

  private drop(peerId: string): void {
    const peer = this.peers.get(peerId);
    if (peer) { try { peer.pc.close(); } catch { /* */ } this.peers.delete(peerId); }
  }
}

export const chatTransport = new ChatTransport();
